import React, { useState } from 'react';
import styled from 'styled-components';
import QcastLogo from "src/assets/brands/qcast_logo.svg";
import QcastApi from "src/store/qcasts/api";

// Types
interface AccessCodeProps {
  qcastId: string;
  onSubmit: (qcast: any) => void | Promise<void>;
  onToggleVisibility?: () => void;
}

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f8f9fa;
  padding: 20px;
`;

const Card = styled.div`
  background: white;
  padding: 48px;
  border-radius: 12px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);
  width: 100%;
  max-width: 400px;
  text-align: center;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 48px;
  
  .logo-m {
    background: #2563eb;
    color: white;
    padding: 8px 12px;
    border-radius: 8px;
    font-weight: bold;
    font-size: 20px;
    margin-right: 4px;
  }
  
  .logo-text {
    font-size: 24px;
    font-weight: 600;
    color: #2563eb;
  }
  
  .logo-shield {
    display: inline-block;
    margin-left: 2px;
    width: 20px;
    height: 20px;
    background: #374151;
    clip-path: polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%);
  }
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: 600;
  color: #374151;
  margin-bottom: 12px;
`;

const Subtitle = styled.p`
  font-size: 16px;
  color: #6b7280;
  margin-bottom: 32px;
`;

const FormGroup = styled.div`
  margin-bottom: 24px;
  text-align: left;
`;

const Label = styled.label`
  display: block;
  font-size: 14px;
  font-weight: 500;
  color: #374151;
  margin-bottom: 8px;
`;

const InputContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  padding-right: 48px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s ease;
  
  &:focus {
    outline: none;
    border-color: #2563eb;
  }
  
  &::placeholder {
    color: #9ca3af;
  }
`;

const ToggleButton = styled.button`
  position: absolute;
  right: 12px;
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  color: #6b7280;
  transition: color 0.2s ease;
  
  &:hover {
    color: #374151;
  }
  
  &:focus {
    outline: none;
  }
`;

const EyeIcon = styled.svg`
  width: 20px;
  height: 20px;
  fill: currentColor;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: #2563eb;
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  
  &:hover {
    background: #1d4ed8;
  }
  
  &:focus {
    outline: none;
    ring: 2px solid #2563eb;
    ring-offset: 2px;
  }
  
  &:disabled {
    background: #9ca3af;
    cursor: not-allowed;
  }
`;

// Component
const AccessCodeComponent: React.FC<AccessCodeProps> = ({ 
  qcastId,
  onSubmit, 
  onToggleVisibility 
}) => {
  const [accessCode, setAccessCode] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsError(false)
    if (!accessCode.trim()) return;
    
    setIsLoading(true);
    
    try {
      const response = await QcastApi.getQcastDeepCopyForPlayer(qcastId, accessCode);
      if (response?.id === 200 && response?.data) {
        await onSubmit(response.data);
        setAccessCode('');
      } else {
        setIsError(true)
      }
    } catch (error) {
      setIsError(true)
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
    onToggleVisibility?.();
  };

  return (
    <Container>
      <Card>
        <Logo>
         <img src={QcastLogo}/>
        </Logo>
        
        <Title>Access Required</Title>
        <Subtitle>Please enter the access code to continue:</Subtitle>
        
        <form onSubmit={handleSubmit}>
          <FormGroup>
            <Label htmlFor="accessCode">Access Code {isError?(<span style={{color:"red"}}>Invalid Code</span>):(null)}</Label>
            <InputContainer>
              <Input
                id="accessCode"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter access code..."
                value={accessCode}
                onChange={(e) => setAccessCode(e.target.value)}
                required
              />
              <ToggleButton
                type="button"
                onClick={togglePasswordVisibility}
                aria-label={showPassword ? 'Hide access code' : 'Show access code'}
              >
                <EyeIcon viewBox="0 0 20 20">
                  {showPassword ? (
                    <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  ) : (
                    <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
                  )}
                  <path d="M10 17c4.478 0 8.268-2.943 9.542-7-1.274-4.057-5.064-7-9.542-7-4.477 0-8.268 2.943-9.542 7 1.274 4.057 5.064 7 9.542 7zM10 15a5 5 0 100-10 5 5 0 000 10z" />
                </EyeIcon>
              </ToggleButton>
            </InputContainer>
          </FormGroup>
          
          <SubmitButton 
            type="submit" 
            disabled={isLoading || !accessCode.trim()}
          >
            {isLoading ? 'Verifying...' : 'Continue'}
          </SubmitButton>
        </form>
      </Card>
    </Container>
  );
};

export default AccessCodeComponent;

// Usage Example:
/*
const App: React.FC = () => {
  const handleAccessCodeSubmit = async (code: string) => {
    // Handle access code submission
    console.log('Access code submitted:', code);
    
    // Example API call
    try {
      const response = await fetch('/api/verify-access', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ accessCode: code }),
      });
      
      if (response.ok) {
        // Handle success
        console.log('Access granted');
      } else {
        // Handle error
        console.error('Access denied');
      }
    } catch (error) {
      console.error('Verification failed:', error);
    }
  };

  return (
    <AccessCodeComponent 
      onSubmit={handleAccessCodeSubmit}
      onToggleVisibility={() => console.log('Visibility toggled')}
    />
  );
};
*/